import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, ClipboardCheck, Info, LifeBuoy, Loader2, LogOut, Mail, Users } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Dossier from "../components/portal/Dossier";
import GroupActivities from "../components/portal/GroupActivities";
import PortfolioCard from "../components/portal/PortfolioCard";
import AssessmentLine from "../components/portal/AssessmentLine";
import FeedbackLine from "../components/portal/FeedbackLine";
import ReferralCard from "../components/portal/ReferralCard";
import { Card, GuideLink, LOGO_COLOR, ReportLink, Shell } from "../components/portal/PortalShell";
import {
  SENT_NOTE, greeting, groupMeta, noticeLines, noticeOpensByItself, portalSections, readOnlyNote,
} from "../lib/portal";
import { isFped } from "../lib/regime";

/**
 * `/portal` — the participants' and trainers' portal (PR 1: the door).
 *
 * One address for everybody: who you are decides what you see. Not signed in,
 * you get the form that sends a link; signed in, your groups.
 */
export default function Portal() {
  const [state, setState] = useState({ phase: "loading" });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/portal/me");
      setState({ phase: "in", me: r.data });
    } catch (err) {
      setState({ phase: err?.response?.status === 401 ? "out" : "error" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.phase === "loading") {
    return (
      <Shell>
        <div className="grid place-items-center py-24 text-[#6B7280]" data-testid="portal-loading">
          <Loader2 className="animate-spin" size={22} />
        </div>
      </Shell>
    );
  }
  if (state.phase === "error") {
    return (
      <Shell>
        <Card>
          <h1 className="font-display text-2xl font-bold text-[#373A45]">Portalul nu răspunde acum</h1>
          <p className="mt-2 text-sm text-[#6B7280]">Încearcă din nou peste câteva momente.</p>
          <button type="button" onClick={load} className={primaryBtn}>Încearcă din nou</button>
        </Card>
      </Shell>
    );
  }
  if (state.phase === "out") return <Shell><RequestLink /></Shell>;
  return <Home me={state.me} onLeft={() => setState({ phase: "out" })} />;
}

const primaryBtn =
  "mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] " +
  "text-white font-semibold py-3 transition-colors disabled:opacity-60 focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-[#965EA7] focus-visible:ring-offset-2";

function RequestLink() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setError("Scrie adresa de email completă.");
      return;
    }
    setSending(true);
    setError("");
    try {
      await api.post("/portal/auth/request", { email: email.trim() });
      setSent(true);
    } catch {
      setError("Nu am putut trimite cererea. Încearcă din nou.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Card>
        <img src={LOGO_COLOR} alt="Fundația pentru Educație Digitală" className="h-11 w-auto" />
        <h1 className="mt-8 font-display text-2xl font-bold tracking-tight text-[#373A45] [text-wrap:balance]">
          Portalul cursului de AI pentru educație
        </h1>
        {sent ? (
          <div data-testid="portal-link-sent">
            <div className="mt-6 flex gap-3 rounded-2xl bg-[#F5F6F6] p-4 text-sm text-[#373A45]">
              <Mail size={18} className="mt-0.5 shrink-0 text-[#965EA7]" />
              <p>{SENT_NOTE}</p>
            </div>
            <button type="button" onClick={() => { setSent(false); setEmail(""); }}
              className="mt-4 text-sm font-semibold text-[#965EA7] hover:underline">
              Am greșit adresa
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
              Intri fără parolă: îți trimitem un link pe email.
            </p>
            <label htmlFor="portal-email" className="mt-6 block text-sm font-semibold text-[#373A45]">
              Adresa de email
            </label>
            <input
              id="portal-email" type="email" autoComplete="email" inputMode="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-[15px] text-[#373A45] focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20"
              data-testid="portal-email"
            />
            <p className="mt-2 text-xs leading-relaxed text-[#6B7280]">
              Folosește adresa din contract. Formatorii folosesc adresa cu care predau.
            </p>
            {error && <p className="mt-3 text-sm text-[#c0483f]" role="alert">{error}</p>}
            <button type="submit" disabled={sending} className={primaryBtn} data-testid="portal-request">
              {sending ? <Loader2 size={16} className="animate-spin" /> : null}
              Trimite-mi linkul
            </button>
          </form>
        )}
      </Card>
      <BackHome />
    </>
  );
}

function BackHome() {
  return (
    <div className="mt-6 flex justify-center">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#965EA7] rounded-full px-3 py-1.5">
        <ArrowLeft size={14} /> Pagina principală
      </Link>
    </div>
  );
}

function Home({ me, onLeft }) {
  const [noticeOpen, setNoticeOpen] = useState(noticeOpensByItself(me));
  const [leaving, setLeaving] = useState(false);
  const sections = portalSections(me);
  const note = readOnlyNote(me);

  const closeNotice = async () => {
    setNoticeOpen(false);
    if (!me.notice_seen_at) {
      try { await api.post("/portal/notice"); } catch { /* shown again next time; nothing lost */ }
    }
  };

  const leave = async () => {
    setLeaving(true);
    try { await api.post("/portal/auth/logout"); } catch { /* the cookie expires on its own */ }
    onLeft();
  };

  return (
    <Shell wide report={false}>
      <header className="flex items-center justify-between gap-4">
        <img src={LOGO_COLOR} alt="Fundația pentru Educație Digitală" className="h-9 w-auto" />
        <div className="flex min-w-0 items-center gap-3">
          <span className="hidden truncate text-sm text-[#6B7280] sm:block" data-testid="portal-email-shown">{me.email}</span>
          <Link to="/portal/ghid"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3.5 py-1.5 text-sm font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7]"
            data-testid="portal-guide-top">
            <LifeBuoy size={14} /> Ghid
          </Link>
          <button type="button" onClick={leave} disabled={leaving}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3.5 py-1.5 text-sm font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7]"
            data-testid="portal-logout">
            <LogOut size={14} /> Ieși
          </button>
        </div>
      </header>

      <h1 className="mt-10 font-display text-3xl font-bold tracking-tight text-[#373A45]" data-testid="portal-greeting">
        {greeting(me)}
      </h1>

      {note && (
        <p className="mt-4 rounded-2xl border border-[#d99a2b]/40 bg-[#d99a2b]/10 p-4 text-sm leading-relaxed text-[#373A45]"
          data-testid="portal-read-only">
          {note}
        </p>
      )}

      {noticeOpen && <Notice me={me} onClose={closeNotice} />}

      {/* The participant's referral code, when the team shows it (13.10.2026). */}
      {(me.participant || []).length > 0 && <ReferralCard />}

      <div className="mt-8 grid gap-8">
        {sections.map((s) => (
          <section key={s.key} aria-labelledby={`portal-${s.key}`}>
            <h2 id={`portal-${s.key}`}
              className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
              {s.title}
            </h2>
            <ul className="mt-3 grid gap-3">
              {s.groups.map((g) => (
                <li key={g.group_id} className="rounded-2xl border border-[#E5E7EB] bg-white p-5"
                  data-testid={`portal-group-${s.key}`}>
                  <div className="flex items-start gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#965EA7]/10 text-[#965EA7]">
                      <Users size={17} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-display text-lg font-bold text-[#373A45]">{g.name}</p>
                      <p className="mt-0.5 text-sm text-[#6B7280]">{groupMeta(g, s.key)}</p>
                    </div>
                  </div>
                  {/* A Curs FPED cohort has no portfolio (12.10.2026). */}
                  {/* The initial assessment, while open (15.10.2026): accredited groups only. */}
                  {s.key === "participant" && !isFped(g) && <AssessmentLine groupId={g.group_id} />}
                  {s.key === "participant" && !isFped(g) && <PortfolioCard groupId={g.group_id} />}
                  {/* The final feedback questionnaire, while open (30.10.2026): every group. */}
                  {s.key === "participant" && <FeedbackLine groupId={g.group_id} />}
                  {s.key === "evaluator" ? (
                    <Link to={`/portal/evaluare/${g.group_id}`} data-testid="portal-evaluation-card"
                      className="mt-4 flex items-center gap-3 rounded-xl border border-[#965EA7]/25 bg-[#965EA7]/[0.04] px-4 py-3 hover:border-[#965EA7]">
                      <ClipboardCheck size={18} className="shrink-0 text-[#965EA7]" />
                      <span className="min-w-0 flex-1 text-sm font-semibold text-[#373A45]">Evaluarea finală: portofoliile și susținerea</span>
                      <ChevronRight size={16} className="shrink-0 text-[#9CA3AF]" />
                    </Link>
                  ) : <GroupActivities groupId={g.group_id} fped={isFped(g)} invited={g.participants_invited !== false} />}
                  {s.key === "participant" && <Dossier groupId={g.group_id} />}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <footer className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[#E5E7EB] pt-5">
        <button type="button" onClick={() => setNoticeOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:underline"
          data-testid="portal-notice-open">
          <Info size={14} /> Cum folosim ce încarci
        </button>
        <GuideLink />
        <ReportLink />
        <Link to="/portal/raportari" className="text-sm text-[#6B7280] hover:text-[#965EA7]" data-testid="portal-my-reports">
          Raportările mele
        </Link>
      </footer>
    </Shell>
  );
}

function Notice({ onClose, me }) {
  return (
    <div className="mt-6 rounded-2xl border border-[#3F8EBA]/30 bg-[#3F8EBA]/[0.06] p-5" role="note"
      data-testid="portal-notice">
      <p className="text-sm font-semibold text-[#373A45]">Cum folosim ce încarci</p>
      <ul className="mt-2 grid gap-1.5 text-sm leading-relaxed text-[#373A45]">
        {noticeLines(me).map((line) => <li key={line}>{line}</li>)}
      </ul>
      <button type="button" onClick={onClose}
        className="mt-4 rounded-full bg-[#373A45] px-4 py-1.5 text-sm font-semibold text-white hover:bg-black"
        data-testid="portal-notice-close">
        Am înțeles
      </button>
    </div>
  );
}
